import { useCallback, useEffect, useReducer, useRef } from 'react';
import { searchPlaceInvoiceService as defaultService } from '../services/searchPlaceInvoiceService';

// ─── State Machine ────────────────────────────────────────────────────────────

const INITIAL_STATE = { status: 'idle', data: null, error: null };

function reducer(state, action) {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, status: 'loading', error: null };
    case 'REFRESH_START':
      return { ...state, status: 'refreshing', error: null };
    case 'FETCH_SUCCESS':
      return { status: 'success', data: action.payload, error: null };
    case 'FETCH_ERROR':
      return { ...state, status: 'error', error: action.payload };
    default:
      return { ...state };
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Low-level composable hook. Executes search_place_invoice whenever `params`
 * reference changes. Callers are responsible for memoizing `params` so that
 * only meaningful filter/pagination changes trigger a new request.
 *
 * Race condition: the requestId ref discards results from stale in-flight
 * requests that resolve after a newer one has already been dispatched.
 *
 * `service` is injected for testability — pass a mock in tests.
 */
export function useGetSearchPlaceInvoice(params, service = defaultService) {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const hasLoadedOnce = useRef(false);
  const requestId = useRef(0);

  const execute = useCallback(
    async forceRefresh => {
      // Skip execution when params is null — caller opts this instance out.
      if (!params) return;
      // eslint-disable-next-line no-plusplus
      const currentId = ++requestId.current;
      dispatch({
        type:
          forceRefresh || hasLoadedOnce.current
            ? 'REFRESH_START'
            : 'FETCH_START',
      });
      try {
        const page = await service.searchPlaceInvoices(params);
        if (currentId !== requestId.current) return;
        hasLoadedOnce.current = true;
        dispatch({ type: 'FETCH_SUCCESS', payload: page });
      } catch (err) {
        if (currentId !== requestId.current) return;
        dispatch({ type: 'FETCH_ERROR', payload: err });
      }
    },
    [service, params],
  );

  useEffect(() => {
    execute(false);
  }, [execute]);

  const refresh = useCallback(() => execute(true), [execute]);

  return {
    status: state.status,
    placeInvoicePage: state.data,
    error: state.error,
    refresh,
  };
}

export default {};
